import {
    ErrorContainer,
    ImagePlaceholderContainer,
    ImagePreviewStack,
    ImagesPreviewsContainer,
    InfoContainer,
    InfoTypography,
} from "./PostForm.styles";
import "swiper/swiper.scss";
import React, { useEffect, useRef, useState } from "react";
import ImageItem from "./ImageItem";
import { Grid2PlusIcon } from "@icons/Grid2PlusIcon";
import { DraggableImage, PostImagesPreview } from "./PostFormModal.types";
import { SortableList } from "../../SortableList/SortableList";

export function ImagesPreview(imagesPreview: PostImagesPreview) {
    const [items, setItems] = useState(
        imagesPreview.images.map((image, id) => ({ id: id + 1, image: image })),
    );
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [startXDrag, setStartXDrag] = useState(0);
    const [scrollLeftPosition, setScrollLeftPosition] = useState(0);
    const [dragAndDropInProgress, setDragAndDropInProgress] = useState(false);

    const scrollAreaRef = useRef<HTMLDivElement>();

    useEffect(() => {
        setItems(
            imagesPreview.images
                .filter((item) => !item.delete)
                .map((image, id) => ({
                    id: id + 1,
                    image: image,
                })),
        );
    }, [imagesPreview.images]);

    const onChange = (newItems: DraggableImage[]) => {
        setItems(newItems);
        imagesPreview.handleDragImage(newItems);
    };

    const onDragAndDropChanged = (dragAndDropInProgress: boolean) => {
        setDragAndDropInProgress(dragAndDropInProgress);
        setIsMouseDown(false);
    };

    function startDrag(event: React.MouseEvent): void {
        if (dragAndDropInProgress) {
            return;
        }
        setIsMouseDown(true);
        if (scrollAreaRef.current) {
            scrollAreaRef.current.style.cursor = "pointer";
            scrollAreaRef.current.style.scrollBehavior = "auto";
            setStartXDrag(event.pageX - scrollAreaRef.current.offsetLeft);
            setScrollLeftPosition(scrollAreaRef.current.scrollLeft);
        }
    }

    function endDrag(): void {
        setIsMouseDown(false);
        if (scrollAreaRef.current) {
            scrollAreaRef.current.style.cursor = "initial";
            scrollAreaRef.current.style.scrollBehavior = "smooth";
        }
    }

    function onMouseMove(event: React.MouseEvent): void {
        event.preventDefault();
        if (isMouseDown && scrollAreaRef && scrollAreaRef.current) {
            const x = event.pageX - scrollAreaRef.current.offsetLeft;
            const scroll = x - startXDrag;
            scrollAreaRef.current.scrollLeft = scrollLeftPosition - scroll;
        }
    }

    if (imagesPreview.isHidden) {
        return <></>;
    }

    const existingImages = imagesPreview.images.filter(
        (image) => !image.delete && image.mediaType === "image",
    );

    return (
        <ImagesPreviewsContainer>
            <ImagePreviewStack
                scrollable={imagesPreview.scrollable}
                direction="row"
                ref={scrollAreaRef}
                onMouseDown={startDrag}
                onMouseUp={endDrag}
                onMouseMove={onMouseMove}
                onMouseLeave={endDrag}
            >
                <SortableList
                    items={items}
                    onChange={onChange}
                    onDragAndDropChanged={onDragAndDropChanged}
                    renderItem={(item) => (
                        <ImageItem
                            itemKey={item.id}
                            image={item.image}
                            handleOnRemoveImage={
                                imagesPreview.handleOnRemoveImage
                            }
                            handleImageUploadRetry={
                                imagesPreview.handleImageUploadRetry
                            }
                        />
                    )}
                />
                {existingImages.length > 0 && existingImages.length < 20 && (
                    <ImagePlaceholderContainer
                        onClick={imagesPreview.triggerInput}
                    >
                        <Grid2PlusIcon
                            color={"var(--community-color-secondary-text)"}
                            width={"24px"}
                            height={"24px"}
                        />
                    </ImagePlaceholderContainer>
                )}
            </ImagePreviewStack>
            {imagesPreview.inappropriateContentDetected && (
                <ErrorContainer>
                    Dein Bildbeitrag wurde aufgrund unserer Richtlinien leider
                    abgelehnt.
                </ErrorContainer>
            )}
            {imagesPreview.images.filter((image) => image.mediaType === "video")
                .length > 0 && (
                <InfoContainer marginTop={"16px"}>
                    <InfoTypography>
                        Dein Video wird im Hintergrund verarbeitet und für die
                        Ausgabe vorbereitet. Dein Beitrag ist für andere Nutzer
                        erst nach Fertigstellung sichtbar.
                    </InfoTypography>
                </InfoContainer>
            )}
        </ImagesPreviewsContainer>
    );
}
